import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { cn } from '../lib/utils';
import { LazySection } from '../components/ui/LazySection';
import { useTranslation } from 'react-i18next';

export function Home() {
  const { t } = useTranslation();
  
  const HERO_SLIDES = useMemo(() => [
    {
      src: '/assets/hero/elSeminario.webp',
      srcSet: '/assets/hero/elSeminario-sm.webp 800w, /assets/hero/elSeminario.webp 1920w',
      title: t("home.hero_slide1_title"),
      description: t("home.hero_slide1_description"),
    },
    {
      src: '/assets/hero/ordenaciones.webp',
      srcSet: '/assets/hero/ordenaciones-sm.webp 800w, /assets/hero/ordenaciones.webp 1920w',
      title: t("home.hero_slide2_title"),
      description: t("home.hero_slide2_description"),
    }
  ], [t]);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 6000); // Change slide every 6 seconds

    return () => clearInterval(interval);
  }, [HERO_SLIDES.length]);

  useEffect(() => {
    // Crossfade animation using GSAP for both image and text simultaneously
    slideRefs.current.forEach((ref, index) => {
      if (ref) {
        gsap.to(ref, {
          opacity: index === currentSlideIndex ? 1 : 0,
          duration: 2.5,
          ease: 'power2.inOut',
          pointerEvents: index === currentSlideIndex ? 'auto' : 'none'
        });
      }
    });
  }, [currentSlideIndex]);

  return (
    <>
      <section className="relative h-[100svh] flex items-center overflow-hidden bg-zinc-950">
        
        {/* Slides (Background + Content) */}
        {HERO_SLIDES.map((slide, index) => (
          <div 
            key={slide.src}
            ref={(el) => {
              if (el) slideRefs.current[index] = el;
            }}
            className="absolute inset-0 opacity-0"
          >
            {/* Background Image */}
            <img 
              src={slide.src}
              srcSet={slide.srcSet}
              sizes="100vw"
              alt={slide.title}
              fetchPriority={index === 0 ? "high" : "auto"}
              loading={index === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center transform scale-100 md:scale-105"
            />
            
            {/* Reduced Gradients for Elegance - Less Exaggerated */}
            {/* Subtle base darkness to ensure white text is readable */}
            <div className="absolute inset-0 bg-zinc-950/20 md:bg-zinc-950/10"></div>
            
            {/* Diagonal fade (much softer now) */}
            <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/90 via-zinc-950/40 md:from-zinc-950/80 md:via-zinc-950/20 to-transparent"></div>
            
            {/* Left side fade for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 via-zinc-950/30 md:from-zinc-950/70 md:via-zinc-950/10 to-transparent w-full md:w-2/3"></div>

            {/* Soft, blurred vignette in bottom-left to seamlessly hide the embedded logo */}
            <div className="absolute -bottom-10 -left-10 w-[500px] h-[400px] bg-zinc-950 blur-[100px] rounded-full z-10 pointer-events-none"></div>
            
            {/* Content aligned left */}
            <div className="absolute inset-0 flex items-center">
              <div className="relative z-10 px-6 sm:px-12 md:px-20 lg:px-32 w-full mt-10 md:mt-20">
                <h1 className="font-neocat text-3xl md:text-5xl leading-tight font-normal mb-4 md:mb-6 uppercase tracking-wide drop-shadow-lg bg-gradient-to-r from-zinc-400 via-zinc-100 to-zinc-500 bg-clip-text text-transparent">
                  {slide.title}
                </h1>
                
                <div className="flex flex-col gap-6 items-start">
                  <p className="font-neocat text-zinc-200 text-sm md:text-lg max-w-sm md:max-w-lg font-light leading-relaxed border-l-[2px] border-zinc-500 pl-4 md:pl-5 py-1 drop-shadow-md">
                    {slide.description}
                  </p>
                  {index === 1 && (
                    <Link
                      to="/noticias/ordenaciones-presbiterales"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/40 rounded-full text-xs font-neocat tracking-widest uppercase transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-white/10"
                    >
                      <span>Ver Noticia</span>
                      <span className="text-zinc-400">→</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Global Bottom Fade (to blend smoothly with the next section) */}
        <div className="absolute inset-x-0 bottom-0 h-32 md:h-40 bg-gradient-to-t from-zinc-950 via-zinc-950/50 md:via-zinc-950/40 to-transparent z-20 pointer-events-none"></div>

        {/* Global Logo in the bottom right corner */}
        <div className="absolute bottom-10 right-6 sm:right-12 md:bottom-12 md:right-20 lg:right-32 z-30 pointer-events-none">
          <img 
            src="/logoCompleto.svg" 
            alt="Seminario Redemptoris Mater" 
            className="h-10 sm:h-14 md:h-28 w-auto opacity-90 drop-shadow-2xl"
            width="300"
            height="112"
          />
        </div>

        {/* Custom minimalist slide indicators */}
        <div className="absolute bottom-12 left-6 sm:left-12 md:left-20 lg:left-32 flex gap-3 md:gap-4 z-30">
           {HERO_SLIDES.map((_, idx) => (
             <button 
               key={idx}
               onClick={() => setCurrentSlideIndex(idx)}
               className="group py-2"
               aria-label={t("home.aria_go_slide", { num: idx + 1 })}
             >
               <div 
                 className={cn(
                   "h-[2px] transition-all duration-700 ease-out rounded-full",
                   currentSlideIndex === idx 
                     ? "w-12 bg-white" 
                     : "w-6 bg-white/30 group-hover:bg-white/60"
                 )}
               />
             </button>
           ))}
        </div>
      </section>
      
      <LazySection className="bg-white py-24 md:py-32 px-4 sm:px-8 md:px-16 lg:px-24 relative z-20" minHeight="800px">
        <div className="max-w-[1400px] mx-auto">
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 md:grid-rows-[350px_350px]">
            
            {/* Card 1: Quiénes Somos (Spans 2 cols, 2 rows) */}
            <div className="group relative overflow-hidden md:col-span-2 md:row-span-2 flex items-end h-[400px] md:h-auto">
              {/* Background */}
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.5s] ease-out group-hover:scale-105" style={{ backgroundImage: "url('/assets/BentoGrid/QuienesSomos.webp')" }}></div>
              {/* Fade for text legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              {/* Content */}
              <div className="relative z-10 p-8 md:p-14">
                <h3 className="font-neocat text-3xl md:text-5xl text-white mb-4 tracking-wide">{t("home.about_title")}</h3>
                <p className="font-neocat text-zinc-200 font-light text-base md:text-lg max-w-2xl leading-relaxed">
                  {t("home.about_description")}
                </p>
              </div>
            </div>

            {/* Card 2: Nuestra Misión (1 col, 1 row) */}
            <div className="group relative overflow-hidden md:col-span-1 md:row-span-1 flex items-end h-[300px] md:h-auto">
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.5s] ease-out group-hover:scale-105" style={{ backgroundImage: "url('/assets/BentoGrid/NuestraMision.webp')" }}></div>
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative z-10 p-8 md:p-10">
                <h3 className="font-neocat text-2xl md:text-3xl text-white mb-3 tracking-wide">{t("home.mission_title")}</h3>
                <p className="font-neocat text-zinc-300 font-light text-sm md:text-base leading-relaxed">
                  {t("home.mission_description")}
                </p>
              </div>
            </div>

            {/* Card 3: Ayúdanos a Ayudar (1 col, 1 row) */}
            <Link to="/donaciones" className="group relative overflow-hidden md:col-span-1 md:row-span-1 flex items-end h-[300px] md:h-auto cursor-pointer block w-full">
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.5s] ease-out group-hover:scale-105" style={{ backgroundImage: "url('/assets/BentoGrid/AyudanosAAyudar.webp')" }}></div>
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative z-10 p-8 md:p-10 w-full">
                <h3 className="font-neocat text-2xl md:text-3xl text-white mb-3 tracking-wide group-hover:underline decoration-1 underline-offset-4">{t("home.help_title")}</h3>
                <p className="font-neocat text-zinc-300 font-light text-sm md:text-base leading-relaxed">
                  {t("home.help_description")}
                </p>
              </div>
            </Link>

          </div>
        </div>
      </LazySection>

      {/* Cinematic Quote Section */}
      <LazySection className="relative min-h-[70svh] md:min-h-[80vh] flex items-center justify-center py-24 px-4 sm:px-12 md:px-20 overflow-hidden bg-zinc-950" minHeight="70svh">
        {/* Parallax Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed opacity-70"
          style={{ backgroundImage: "url('/assets/001.webp')" }}
        ></div>
        
        {/* Dark Overlays for moody, solemn aesthetic */}
        <div className="absolute inset-0 bg-zinc-950/60 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-transparent to-zinc-950 opacity-90"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center">
          <span className="font-neocat text-7xl md:text-[120px] text-zinc-600/40 leading-none h-12 md:h-20 overflow-hidden select-none">“</span>
          <p className="font-neocat text-lg md:text-2xl lg:text-3xl text-zinc-200 leading-relaxed tracking-wide mt-4 md:mt-8 px-4 drop-shadow-lg max-w-4xl">
            {t("home.quote_text")}
          </p>
          <div className="w-16 md:w-24 h-[1px] bg-zinc-600 mt-10 md:mt-12"></div>
        </div>
      </LazySection>
    </>
  );
}
